import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Space,
  message,
  Divider,
  Input,
  Spin,
  Card as AntCard,
  Select,
  Descriptions,
  Image as AntImage,
  Table,
} from 'antd';
import { MedicineBoxOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { helper } from '@src/controls/controlHelper';
import SingleSelect from '@src/packages/pro-component/schema/SingleSelect';
import ImageModel from '@src/packages/pro-component/schema/Image';
import TextArea from 'antd/lib/input/TextArea';
import dayjs from 'dayjs';
import DatePickerComponent from '@src/packages/pro-component/components/DatePicker';

const { Title } = Typography;
const PAGE_ID = 340; // New page ID for doctor management

export default function ResultBookedSlotFormCtrl(props: any) {
  console.log('🚀 ~ CustomerFormCtrl ~ props:', props);
  const { onClose } = props;
  const params = props.query || {};
  const { page, mode, id } = params;
  const [form] = Form.useForm();
  const [pageId, setPageId] = useState<number>(page || PAGE_ID);
  const [, setPageInfo] = useState<any>();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [provinceName, setProvinceName] = useState<string>('');
  const [wardName, setWardName] = useState<string>('');

  useEffect(() => {
    const initData = async () => {
      try {
        const _pageInfo = await helper.getPage(pageId);
        setPageInfo(_pageInfo);

        if (_pageInfo && id) {
          await getResultBookedSlotInfo(id, _pageInfo);
          setDataLoaded(true);
        }
      } catch (error) {
        console.log('Error initializing data: ', error);
        message.error('Có lỗi xảy ra khi tải thông tin');
      }
    };

    initData();
  }, []);

  const getResultBookedSlotInfo = useCallback(
    async (resultBookedSlotId: number, _pageInfo: any) => {
      try {
        const response: any = await helper.callPageApi(
          _pageInfo,
          'findBookedSlot',
          {
            queryInput: JSON.stringify({ id: resultBookedSlotId }),
            skip: 0,
            limit: 1,
            count: false,
          }
        );
        console.log('🚀 ~ ResultBookedSlotFormCtrl ~ response:', response);

        if (response.status === 200) {
          const data = response.data.data[0];

          form.setFieldsValue({
            name: data.customerName,
            phone: data.customerPhone,
            avatar: data.customerAvatar,
            gender: data.customerGender,
            birth: data.customerBirth ? dayjs(data.customerBirth) : dayjs(),
            bookedResult:
              data.resultBookedSlot?.length > 0
                ? data.resultBookedSlot[0]
                : null,
          });
        } else {
          throw new Error('Failed to fetch result booked slot info');
        }
      } catch (error) {
        console.log('Error fetching result booked slot info: ', error);
        message.error('Có lỗi xảy ra khi tải thông tin và kết quả khám');
      }
    },
    [form]
  );

  const isView = mode === 'view' || props.viewOnly;
  const genderLabelMap: Record<string, string> = {
    MALE: 'Nam',
    FEMALE: 'Nữ',
    OTHER: 'Khác',
  };
  const handleCancel = () => {
    onClose();
  };
  if (!dataLoaded) {
    return (
      <div className='text-center p-5'>
        <Spin size='large' tip='Đang tải dữ liệu...' />
      </div>
    );
  }

  if (isView) {
    const values = form.getFieldsValue(true);
    console.log('🚀 ~ values:', values);
    return (
      <div className='doctor-form-container'>
        <Card className='mb-4 custom-card'>
          <Descriptions bordered column={1} title='Thông tin khách hàng'>
            <Descriptions.Item label='Họ và tên'>
              {values?.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label='Số điện thoại'>
              {values?.phone || '-'}
            </Descriptions.Item>
            <Descriptions.Item label='Giới tính'>
              {genderLabelMap[values?.gender] || '-'}
            </Descriptions.Item>
            <Descriptions.Item label='Năm sinh'>
              {values?.birth ? dayjs(values.birth).format('DD/MM/YYYY') : '-'}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Descriptions bordered column={1} title='Kết luận'>
            <Descriptions.Item label='Bệnh sử'>
              {values?.bookedResult?.caseHistory || '-'}
            </Descriptions.Item>
            <Descriptions.Item label='Chẩn đoán'>
              {values?.bookedResult?.diagnosis || '-'}
            </Descriptions.Item>
            <Descriptions.Item label='Ghi chú khám'>
              {values?.bookedResult?.note || '-'}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          {/* Replace the existing Descriptions section with Table */}
          <div style={{ marginBottom: 16 }}>
            <Title level={5} style={{ marginBottom: 16 }}>
              <MedicineBoxOutlined style={{ marginRight: 8 }} />
              Đơn thuốc
            </Title>

            {values?.bookedResult?.prescription?.length > 0 ? (
              <Table
                dataSource={values.bookedResult.prescription}
                columns={[
                  {
                    title: 'Tên thuốc',
                    dataIndex: 'medicine',
                    key: 'medicine',
                    width: '30%',
                  },
                  {
                    title: 'Giá',
                    dataIndex: 'price',
                    key: 'price',
                    width: '20%',
                    render: (price: number) =>
                      price ? `${price.toLocaleString('vi-VN')} VNĐ` : '-',
                  },
                  {
                    title: 'Số lượng',
                    dataIndex: 'quantity',
                    key: 'quantity',
                    width: '15%',
                    render: (quantity: number) => quantity || '-',
                  },
                  {
                    title: 'Ghi chú',
                    dataIndex: 'note',
                    key: 'note',
                    width: '35%',
                    render: (note: string) => note || '-',
                  },
                ]}
                pagination={false}
                size='small'
                bordered
                rowKey={(record, index) => index?.toString() || ''}
              />
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: '#999',
                  fontStyle: 'italic',
                }}
              >
                Không có thuốc được kê đơn
              </div>
            )}
          </div>
        </Card>

        <div
          className='d-flex justify-content-end mt-4'
          style={{ textAlign: 'right', marginTop: 16 }}
        >
          <Space>
            <Button onClick={handleCancel}>Đóng</Button>
          </Space>
        </div>
      </div>
    );
  } else {
    return (
      <div className='doctor-form-container'>
        <Form
          form={form}
          layout='vertical'
          onFinish={() => {}}
          className='doctor-form'
        >
          <Card className='mb-4 custom-card'>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name='avatar' label={'Ảnh đại diện'}>
                  <ImageModel
                    onChange={(value) => {
                      form.setFieldsValue({
                        avatar: value,
                      });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='name' label='Họ và tên'>
                  <Input size='large' />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='phone' label='Số điện thoại'>
                  <Input size='large' />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='gender' label='Giới tính'>
                  <Select
                    options={[
                      { label: 'Nam', value: 'MALE' },
                      { label: 'Nữ', value: 'FEMALE' },
                      { label: 'Khác', value: 'OTHER' },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item name='birth' label='Năm sinh'>
                  <DatePickerComponent
                    showTime
                    value={
                      form.getFieldValue('birth')
                        ? dayjs(form.getFieldValue('birth'))
                        : dayjs()
                    }
                    format='DD/MM/YYYY'
                    disabled={true}
                    style={{ width: '100%' }}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='occupation' label='Nghề nghiệp'>
                  <Input size='large' />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* Professional Information */}
            <Title level={5} style={{ marginBottom: 16 }}>
              <InfoCircleOutlined style={{ marginRight: 8 }} />
              THÔNG TIN
            </Title>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name='provinceId' label='Tỉnh/Thành phố'>
                  <SingleSelect
                    schema={{
                      pageId: pageId,
                      api: 'findProvince',
                      modelSelectField: 'id$$ID,name$$Tỉnh/Thành phố',
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name='wardId' label='Phường/Xã'>
                  <SingleSelect
                    schema={{
                      pageId: pageId,
                      api: 'findWard',
                      modelSelectField: 'id$$ID,name$$Phường/Xã',
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  name='socialInsuranceNumber'
                  label='Số sổ bảo hiểm y tế'
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='otherInsuranceNumber' label='Số bảo hiểm khác'>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='bloodType' label='Nhóm máu'>
                  <Select
                    options={[
                      { label: 'A+', value: 'A' },
                      { label: 'A-', value: 'A-' },
                      { label: 'B+', value: 'B' },
                      { label: 'B-', value: 'B-' },
                      { label: 'AB+', value: 'AB' },
                      { label: 'AB-', value: 'AB-' },
                      { label: 'O+', value: 'O' },
                      { label: 'O-', value: 'O-' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Title level={5} style={{ marginBottom: 16 }}>
              <MedicineBoxOutlined style={{ marginRight: 8 }} />
              TIỀN SỬ BỆNH
            </Title>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name='birthDefects' label='Dị tật bẩm sinh'>
                  <TextArea size='large' rows={4} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name='internalMedicalHistory'
                  label='Tiền sử bệnh lý nội khoa'
                >
                  <TextArea size='large' rows={4} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='surgicalHistory' label='Tiền sử phẫu thuật'>
                  <TextArea size='large' rows={4} />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name='sufferersAllergy'
                  label='Tiền sử gia đình - [dị ứng - người mắc]'
                >
                  <TextArea size='large' rows={4} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name='suffererDisease'
                  label='Tiền sử gia đình - [bệnh tật -- người mắc]'
                >
                  <TextArea size='large' rows={4} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='otherProblems' label='Vấn đề khác'>
                  <TextArea size='large' rows={4} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <div
            className='d-flex justify-content-end mt-4'
            style={{ textAlign: 'right', marginTop: 16 }}
          >
            <Space>
              <Button onClick={handleCancel}>Hủy</Button>
            </Space>
          </div>
        </Form>
      </div>
    );
  }
}
